import React, { useMemo } from 'react';
import { propertyTypeMappings, getPropertyTypesById } from 'helpers/property_types';
import ManagedInput from 'components/form/managed_input';
import ToggleOverrideSection from '../shared/toggle_override_section';
import { Alert } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUndo } from '@fortawesome/pro-solid-svg-icons';
import { isEqual } from 'lodash';

interface PropertyTypePickerProps {
  defaultPropertyTypes?: number[] | null;
  defaultPropertySubtypes?: number[] | null;
  selectAllPropertyTypes?: boolean;
  selectedPropertyTypes?: number[] | null;
  selectedPropertySubtypes?: number[] | null;
  onUpdatePropertyType: (payload: {
    propertyTypes?: number[],
    propertySubtypes?: number[],
    selectAllPropertyTypes?: boolean
  }) => void;
}

export default function PropertyTypePicker({
  defaultPropertyTypes,
  defaultPropertySubtypes,
  selectedPropertyTypes = [],
  selectedPropertySubtypes = [],
  selectAllPropertyTypes,
  onUpdatePropertyType
}: PropertyTypePickerProps) {
  const propertyTypesById = useMemo(() => getPropertyTypesById(), []);

  const handleSetDefaults = () => {
    onUpdatePropertyType({
      selectAllPropertyTypes: false,
      propertyTypes: defaultPropertyTypes || [],
      propertySubtypes: defaultPropertySubtypes || []
    });
  };

  const handleUpdatePropertyType = (id: number, value: boolean) => {
    const propertyType = propertyTypesById[id];
    const subtypeIds = propertyType ? Object.keys(propertyType.subtypesById).map(Number) : [];

    if (value) {
      // When checking: add parent type ID and clear all subtypes for this type
      onUpdatePropertyType({
        propertyTypes: [...(selectedPropertyTypes || []), id],
        propertySubtypes: (selectedPropertySubtypes || []).filter(typeId => !subtypeIds.includes(typeId))
      });
    } else {
      // When unchecking: remove the parent type ID and all subtypes for this type
      onUpdatePropertyType({
        propertyTypes: (selectedPropertyTypes || []).filter(typeId => typeId !== id),
        propertySubtypes: (selectedPropertySubtypes || []).filter(typeId => !subtypeIds.includes(typeId))
      });
    }
  };

  const handleUpdatePropertySubtype = (subtypeId: number, value: boolean) => {
    const parentPropertyTypeId = Math.floor(subtypeId / 100);
    const propertyType = propertyTypesById[parentPropertyTypeId];
    const allSubtypeIds = propertyType ? Object.keys(propertyType.subtypesById).map(Number) : [];

    const currentTypes = [...(selectedPropertyTypes || [])];
    const currentSubtypes = [...(selectedPropertySubtypes || [])];

    if (value) {
      // Remove parent type ID if it exists and add the subtype
      const filteredTypes = currentTypes.filter(typeId => typeId !== parentPropertyTypeId);
      const newSubtypes = [...currentSubtypes, subtypeId];

      // Check if all subtypes are now selected
      const selectedSubtypesForParent = newSubtypes.filter(id => allSubtypeIds.includes(id));
      if (selectedSubtypesForParent.length === allSubtypeIds.length) {
        // All subtypes selected, replace with parent type ID
        onUpdatePropertyType({
          propertyTypes: [...filteredTypes, parentPropertyTypeId],
          propertySubtypes: newSubtypes.filter(id => !allSubtypeIds.includes(id))
        });
      } else {
        onUpdatePropertyType({
          propertyTypes: filteredTypes,
          propertySubtypes: newSubtypes
        });
      }
    } else {
      if (currentTypes.includes(parentPropertyTypeId)) {
        const withoutParent = currentTypes.filter(typeId => typeId !== parentPropertyTypeId);
        const otherSubtypes = allSubtypeIds.filter(id => id !== subtypeId);
        onUpdatePropertyType({
          propertyTypes: withoutParent,
          propertySubtypes: [...currentSubtypes, ...otherSubtypes]
        });
      } else {
        onUpdatePropertyType({
          propertyTypes: currentTypes,
          propertySubtypes: currentSubtypes.filter(typeId => typeId !== subtypeId)
        });
      }
    }
  };

  const canSetDefault : boolean = useMemo(() => {
    const typesChanged = !!defaultPropertyTypes &&
                           defaultPropertyTypes.length > 0 &&
                           !selectAllPropertyTypes &&
                           !isEqual(defaultPropertyTypes?.sort(), selectedPropertyTypes?.sort());
    const subtypesChanged = !!defaultPropertySubtypes && defaultPropertySubtypes.length > 0 &&
                            !isEqual(defaultPropertySubtypes?.sort(), selectedPropertySubtypes?.sort());
    return typesChanged || subtypesChanged;
  }, [
    defaultPropertyTypes,
    defaultPropertySubtypes,
    selectAllPropertyTypes,
    selectedPropertyTypes,
    selectedPropertySubtypes
  ]);

  const renderDefaultAlert = () => {
    if (!canSetDefault) return null;

    return (
      <Alert className="text-center" variant="info">
        <div>
          The property types selected have been edited from the initial configuration.
          Selecting "revert" below will undo those changes,
          and make this document visible to property types selected when this template was created.
        </div>
        <div className="btn btn-link btn-sm" onClick={handleSetDefaults}>
          <FontAwesomeIcon className="me-2" icon={faUndo} />
          Revert Available Property Types
        </div>
      </Alert>
    );
  };

  return (
    <React.Fragment>
      <ToggleOverrideSection
        checked={selectAllPropertyTypes}
        name="property-type-override"
        subtitle={selectAllPropertyTypes ?
          'Unchecking will allow you to grant access to this document template on a per property type basis' :
          'Checking will grant access to this document template for all property types'
        }
        title="Enable for all property types"
        onOverride={handleSetDefaults}
        onRemove={() => onUpdatePropertyType({ selectAllPropertyTypes: true })}
      />
      {renderDefaultAlert()}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
        {!selectAllPropertyTypes && Object.entries(propertyTypeMappings).map(([id, label]) => {
          const propertyTypeId = parseInt(id);
          const propertyType = propertyTypesById[propertyTypeId];
          const subtypes: Array<{ id: number; label: string }> = propertyType ?
            Object.values(propertyType.subtypesById) : [];

          const parentTypeSelected = (selectedPropertyTypes || []).includes(propertyTypeId);

          const selectedSubtypeCount = subtypes.filter(
            subtype => (selectedPropertySubtypes || []).includes(subtype.id)
          ).length;

          const allSubtypesSelected = parentTypeSelected || (
            subtypes.length > 0 && selectedSubtypeCount === subtypes.length
          );
          const someSubtypesSelected = !parentTypeSelected &&
            selectedSubtypeCount > 0 &&
            selectedSubtypeCount < subtypes.length;
          const isPropertyTypeChecked = allSubtypesSelected;

          return (
            <div key={id}>
              <ManagedInput
                checked={isPropertyTypeChecked}
                indeterminate={someSubtypesSelected}
                label={label}
                name={id}
                type="checkbox"
                onValueChange={(value: boolean) => handleUpdatePropertyType(propertyTypeId, value)}
              >
                {label}
              </ManagedInput>
              {subtypes.length > 0 && (
                <div className="d-flex flex-column">
                  {subtypes.map((subtype) => {
                    const isSubtypeChecked = parentTypeSelected ||
                      (selectedPropertySubtypes || []).includes(subtype.id);
                    return (
                      <ManagedInput
                        checked={isSubtypeChecked}
                        key={subtype.id}
                        label={subtype.label}
                        name={String(subtype.id)}
                        type="checkbox"
                        onValueChange={(value: boolean) => handleUpdatePropertySubtype(subtype.id, value)}
                      >
                        <small className="text-muted">{subtype.label}</small>
                      </ManagedInput>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </React.Fragment>
  );
}
